import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NavContainer from './Nav';
import OffCanvasNav from './OffCanvasNav';
import { saveSaleDraft, getSaleDraft, clearSaleDraft, hasSaleDraft } from '../utils/saleDraftStorage';
import { dotoniwoofApi } from '../api/dotoniwooApi';
import { 
    Card, 
    Col, 
    Container, 
    Form, 
    Row, 
    Table, 
    Button, 
    Modal, 
    Alert, 
    Spinner,
    InputGroup,
    ListGroup,
    Badge
} from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Pagination from './Pagination';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import AlertModal from './AlertModal';
import useAlertModal from '../hooks/useAlertModal';
import './ModernRegisterSale.css';

const LoadingOverlay = ({ show }) => (
    show && (
        <div className="loading-overlay">
            <Spinner animation="border" variant="light" />
        </div>
    )
);

const ProductImageThumb = ({ url, alt }) => {
    const [hasError, setHasError] = React.useState(false);
    const showImage = url && url.trim() && !hasError;
    return (
        <div className="product-image-container">
            {showImage ? (
                <img
                    src={url}
                    alt={alt}
                    className="product-thumb-img"
                    onError={() => setHasError(true)}
                />
            ) : (
                <div className="product-image-placeholder">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="currentColor" opacity="0.5"/>
                    </svg>
                </div>
            )}
        </div>
    );
};

/** Descripción del item con atributos (peso, color, talla). Omite los que son N/A. Ej: "Chunky 1Kg" */
const getItemDisplayName = (item) => {
    const base = item?.description || item?.product?.name || '';
    const attrs = [];
    const w = item?.weight?.weight_name;
    if (w && String(w).toUpperCase() !== 'N/A') attrs.push(w);
    const c = item?.color?.color_name;
    if (c && String(c).toUpperCase() !== 'N/A') attrs.push(c);
    const s = item?.size?.size_name;
    if (s && String(s).toUpperCase() !== 'N/A') attrs.push(s);
    return attrs.length > 0 ? `${base} ${attrs.join(' ')}` : base;
};

const ModernRegisterSale = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [payments, setPayments] = useState([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchSKU, setSKU] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [showModalPurchase, setShowModalPurchase] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('');
    const { showAlert, showAlertModal, alertMessage, alertType, hideAlert } = useAlertModal();
    const [showPriceModal, setShowPriceModal] = useState(false);
    const [selectedItemForPrice, setSelectedItemForPrice] = useState(null);
    const [salePrice, setSalePrice] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerAmount, setCustomerAmount] = useState('');
    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [uniqueKey, setUniqueKey] = useState(0);
    const [responseData, setResponseData] = useState(null);
    const [showDraftModal, setShowDraftModal] = useState(false);

    const itemsPerPage = 60;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    // Función para obtener la fecha local en formato YYYY-MM-DD
    const getLocalDateString = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Obtener la fecha actual
    const [currentDate, setCurrentDate] = useState(getLocalDateString());
    
    // Estados para el modal de abonos
    const [showInstallmentModal, setShowInstallmentModal] = useState(false);
    const [installmentData, setInstallmentData] = useState({
        phone: '',
        name: '',
        amount: ''
    });

    // Estados para el teclado virtual
    const [showKeyboard, setShowKeyboard] = useState(false);
    const [keyboardType, setKeyboardType] = useState('default'); // 'default' o 'numeric'
    const [inputName, setInputName] = useState('');
    const keyboardRef = useRef(null);

    const formatter = new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });

    // Calcular totales con IVA por item usando rate
    const calculateTotals = () => {
        let subtotal = 0;
        let totalIVA = 0;
        let totalGeneral = 0;

        selectedItems.forEach(item => {
            const itemSalePrice = parseFloat(item.salePrice || item.price); // Usar salePrice si existe
            const itemQuantity = parseInt(item.quantity || 1); // Cantidad del item
            const itemRate = parseFloat(item.rate) || 0; // Rate viene como decimal (ej: 0.05 = 5%)
            
            // Calcular subtotal e IVA desde el precio de venta por cantidad
            const itemSubtotalPerUnit = itemSalePrice / (1 + itemRate); // Precio sin IVA por unidad
            const itemIVAPerUnit = itemSubtotalPerUnit * itemRate; // IVA por unidad
            const itemTotalPerUnit = itemSalePrice; // Total por unidad
            
            // Multiplicar por cantidad
            const itemSubtotal = itemSubtotalPerUnit * itemQuantity;
            const itemIVA = itemIVAPerUnit * itemQuantity;
            const itemTotal = itemTotalPerUnit * itemQuantity;

            subtotal += itemSubtotal;
            totalIVA += itemIVA;
            totalGeneral += itemTotal;
        });

        return {
            subtotal,
            totalIVA,
            totalGeneral,
            totalProducts: selectedItems.length
        };
    };

    const totals = calculateTotals();

    const filteredItems = items.filter((item) => {
        const nameMatch = item.product.name.toLowerCase().includes(searchTerm.toLowerCase());
        const skuMatch = item.product.sku?.toLowerCase().includes(searchTerm.toLowerCase());
        const barcodeMatch = searchSKU && item.product.sku?.toLowerCase().includes(searchSKU.toLowerCase());
        
        return nameMatch || skuMatch || barcodeMatch;
    });

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

    const handleAddToCart = (item) => {
        setSelectedItemForPrice(item);
        // Dejar el campo vacío para facilitar la entrada
        setSalePrice('');
        setQuantity(1); // Inicializar cantidad en 1
        setShowPriceModal(true);
    };

    const confirmAddToCart = () => {
        if (!selectedItemForPrice || !salePrice) return;
        
        setUniqueKey(uniqueKey + 1);
        const itemWithKey = { 
            ...selectedItemForPrice, 
            key: uniqueKey,
            salePrice: parseFloat(salePrice), // Precio de venta con IVA incluido
            quantity: parseInt(quantity) // Cantidad del item
        };
        setSelectedItems((prevItems) => [...prevItems, itemWithKey]);
        
        // Cerrar modal y limpiar
        setShowPriceModal(false);
        setSelectedItemForPrice(null);
        setSalePrice('');
        setQuantity(1);
    };

    const cancelAddToCart = () => {
        setShowPriceModal(false);
        setSelectedItemForPrice(null);
        setSalePrice('');
        setQuantity(1);
    };

    const removeFromCart = (key) => {
        setSelectedItems(selectedItems.filter(item => item.key !== key));
    };

    const updateQuantity = (key, newQuantity) => {
        if (newQuantity <= 0) {
            removeFromCart(key);
            return;
        }
        
        setSelectedItems(selectedItems.map(item =>
            item.key === key ? { ...item, quantity: parseInt(newQuantity) } : item
        ));
    };

    const clearCart = () => {
        setSelectedItems([]);
        setCustomerSearch('');
        setSelectedCustomer(null);
        setPaymentMethod('');
        setInstallmentData({ phone: '', name: '', amount: '' });
    };

    // --- Borrador de venta ---
    const handleSaveDraft = () => {
        if (selectedItems.length === 0) {
            showAlert('No hay items en el carrito para guardar', 'error');
            return;
        }
        const saved = saveSaleDraft(user?.uid, {
            selectedItems,
            paymentMethod,
            customerSearch,
            selectedCustomer,
            installmentData,
            uniqueKey,
        });
        if (saved) {
            showAlert('Venta guardada como borrador. Puedes continuar más tarde.', 'success');
        } else {
            showAlert('Error al guardar el borrador', 'error');
        }
    };

    const handleGoToCreateProduct = () => {
        if (selectedItems.length > 0) {
            handleSaveDraft();
        }
        navigate('/creacion-producto');
    };

    const handleRestoreDraft = () => {
        const draft = getSaleDraft(user?.uid);
        if (!draft) return;
        setSelectedItems(draft.selectedItems || []);
        setPaymentMethod(draft.paymentMethod || '');
        setCustomerSearch(draft.customerSearch || '');
        setSelectedCustomer(draft.selectedCustomer || null);
        setInstallmentData(draft.installmentData || { phone: '', name: '', amount: '' });
        setUniqueKey(draft.uniqueKey ?? 0);
        clearSaleDraft(user?.uid);
        setShowDraftModal(false);
        showAlert('Venta recuperada. Puedes continuar agregando items.', 'success');
    };

    const handleDiscardDraft = () => {
        clearSaleDraft(user?.uid);
        setShowDraftModal(false);
    };

    // Al montar: verificar si hay borrador guardado
    useEffect(() => {
        if (user?.uid && hasSaleDraft(user.uid)) {
            setShowDraftModal(true);
        }
    }, [user?.uid]);

    // Funciones para el teclado virtual
    const handleInputFocus = (name, type) => {
        console.log('Opening keyboard for:', name, 'type:', type);
        setInputName(name);
        setKeyboardType(type === 'number' || type === 'tel' ? 'numeric' : 'default');
        setShowKeyboard(true);
    };

    const handleCloseKeyboard = () => {
        console.log('Closing keyboard');
        setShowKeyboard(false);
    };

    const handleKeyboardChange = (input) => {
        // Actualizar el valor del input correspondiente
        switch(inputName) {
            case 'searchTerm':
                setSearchTerm(input);
                break;
            case 'searchSKU':
                setSKU(input);
                break;
            case 'salePrice':
                setSalePrice(input);
                break;
            case 'quantity':
                setQuantity(input);
                break;
            case 'customerSearch':
                setCustomerSearch(input);
                break;
            case 'installmentPhone':
                setInstallmentData({...installmentData, phone: input});
                break;
            case 'installmentName':
                setInstallmentData({...installmentData, name: input});
                break;
            case 'installmentAmount':
                setInstallmentData({...installmentData, amount: input});
                break;
            default:
                break;
        }
    };

    // Actualizar el teclado virtual solo si está abierto
    useEffect(() => {
        if (keyboardRef.current && showKeyboard) {
            const value = getCurrentInputValue();
            keyboardRef.current.setInput(value);
        }
    }, [searchTerm, searchSKU, salePrice, quantity, installmentData.phone, installmentData.name, installmentData.amount]);

    const getCurrentInputValue = () => {
        switch(inputName) {
            case 'searchTerm': return searchTerm;
            case 'searchSKU': return searchSKU;
            case 'salePrice': return salePrice;
            case 'quantity': return quantity.toString();
            case 'customerSearch': return customerSearch;
            case 'installmentPhone': return installmentData.phone;
            case 'installmentName': return installmentData.name;
            case 'installmentAmount': return installmentData.amount;
            default: return '';
        }
    };

    const handleCreateCustomer = () => {
        // Redirigir a la gestión de clientes
        window.open('/gestion-clientes', '_blank');
    };

    const handleCloseModal = () => {
        setShowModalPurchase(false);
    };

    const handleSearchCustomer = async () => {
        if (!customerSearch.trim()) {
            showAlert('Por favor ingrese un nombre o teléfono para buscar', 'error');
            return;
        }

        setCustomerLoading(true);
        try {
            await dotoniwoofApi.get(`/customer.php?id=${customerSearch.trim()}`)
                .then(res => {
                    let responseApi = res.data;
                    if (responseApi.code === 200) {
                        setSelectedCustomer(responseApi.message);
                        setCustomerSearch(responseApi.message.name || responseApi.message.customer_name || 'Cliente encontrado');
                    } else {
                        setSelectedCustomer(null);
                        showAlert('Cliente no encontrado', 'error');
                    }
                })
                .catch(err => {
                    console.log(err);
                    setSelectedCustomer(null);
                    showAlert('Error al buscar el cliente', 'error');
                });
        } catch (error) {
            console.log(error);
            setSelectedCustomer(null);
            showAlert('Error al buscar el cliente', 'error');
        } finally {
            setCustomerLoading(false);
        }
    };

    useEffect(() => {
        getItems();
        getPayments();
    }, []);

    // Resetear página cuando cambie el filtro
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, searchSKU]);

    // Escáner de código de barras
    useEffect(() => {
        let buffer = '';
        let timeout;
      
        const handleKeyDown = (e) => {
          clearTimeout(timeout);
      
          if (e.key === 'Enter') {
            setSKU(buffer);
            buffer = '';
          } else {
            buffer += e.key;
            timeout = setTimeout(() => buffer = '', 300);
          }
        };
      
        window.addEventListener('keydown', handleKeyDown);
      
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const getItems = async () => {
        setLoading(true);
        try {
            const res = await dotoniwoofApi.get(`/item.php`);
            const responseApi = res.data;
            if (responseApi.code === 200) {
                setItems(responseApi.message);
            }
        } catch (err) {
            console.error('Error al cargar productos:', err);
        } finally {
            setLoading(false);
        }
    };

    const getPayments = async () => {
        setPaymentsLoading(true);
        try {
            console.log('Cargando métodos de pago...');
            const res = await dotoniwoofApi.get(`/paymentMethod.php/sales`);
            const responseApi = res.data;
            console.log('Respuesta de métodos de pago:', responseApi);
            if (responseApi.code === 200) {
                setPayments(responseApi.message);
                console.log('Métodos de pago cargados:', responseApi.message);
            } else {
                console.error('Error en respuesta de métodos de pago:', responseApi);
                // Métodos de pago por defecto
                setPayments([
                    { id: 1, payment_method_name: 'Efectivo' },
                    { id: 2, payment_method_name: 'Tarjeta de Débito' },
                    { id: 3, payment_method_name: 'Tarjeta de Crédito' },
                    { id: 4, payment_method_name: 'Transferencia' }
                ]);
            }
        } catch (err) {
            console.error('Error al cargar métodos de pago:', err);
            // Intentar con endpoint alternativo
            try {
                const res = await dotoniwoofApi.get(`/paymentMethod.php`);
                const responseApi = res.data;
                console.log('Respuesta alternativa:', responseApi);
                if (responseApi.code === 200) {
                    setPayments(responseApi.message);
                    console.log('Métodos de pago cargados (alternativo):', responseApi.message);
                } else {
                    // Métodos de pago por defecto
                    setPayments([
                        { id: 1, payment_method_name: 'Efectivo' },
                        { id: 2, payment_method_name: 'Tarjeta de Débito' },
                        { id: 3, payment_method_name: 'Tarjeta de Crédito' },
                        { id: 4, payment_method_name: 'Transferencia' }
                    ]);
                }
            } catch (altErr) {
                console.error('Error en endpoint alternativo:', altErr);
                // Métodos de pago por defecto
                setPayments([
                    { id: 1, payment_method_name: 'Efectivo' },
                    { id: 2, payment_method_name: 'Tarjeta de Débito' },
                    { id: 3, payment_method_name: 'Tarjeta de Crédito' },
                    { id: 4, payment_method_name: 'Transferencia' }
                ]);
            }
        } finally {
            setPaymentsLoading(false);
        }
    };

    const handleSubmitPurchase = async () => {
        // Verificar si el método de pago es ABONOS
        const selectedPaymentMethod = payments.find(p => p.id_payment_method == paymentMethod);
        const isInstallment = selectedPaymentMethod?.method_name?.toUpperCase() === 'ABONOS';
        
        if (isInstallment) {
            // Abrir modal de abonos
            setShowInstallmentModal(true);
            return;
        }
        
        try {
            setLoading(true);
            setShowModalPurchase(false); // Cerrar modal de compra
            
            // Transformar items para enviar el precio total (precio × cantidad)
            const transformedItems = selectedItems.map(item => {
                const unitPrice = item.salePrice || item.price; // Precio unitario del modal
                const totalPrice = unitPrice ; // Precio total por cantidad
                
                return {
                    ...item,
                    price: totalPrice, // Precio total (unitario × cantidad)
                    unitPrice: unitPrice, // Precio unitario para referencia
                    originalPrice: item.price // Precio base original
                };
            });

            const saleData = {
                firebase_UID: user.uid,
                date: getLocalDateString(), // Formato YYYY-MM-DD en hora local
                items: transformedItems,
                total: totals.totalGeneral,
                paymentMethod: paymentMethod
            };

            console.log('Enviando datos de venta:', saleData);
            
            const { data } = await dotoniwoofApi.post("/sale.php", saleData);
            
            if (data.code === 201) {
                showAlert('Venta registrada exitosamente', 'success');
                generatePDF();
                clearCart();
                clearSaleDraft(user?.uid);
                getItems(); // Recargar items para actualizar stock
            } else {
                throw new Error('Error al procesar la venta');
            }
        } catch (error) {
            console.error('Error al procesar la venta:', error);
            showAlert('Error al procesar la venta. Por favor, intente nuevamente.', 'error');
        } finally {
            setLoading(false);  
        }
    };

    const handleCreateInstallment = async () => {
        try {
            setLoading(true);
            setShowInstallmentModal(false);
            
            const currentDate = getLocalDateString(); // Formato YYYY-MM-DD en hora local
            
            // PASO 1: Crear la venta
            const transformedItems = selectedItems.map(item => ({
                id_item: item.id_item,
                price: parseFloat(item.salePrice || item.price)
            }));

            const salePayload = {
                firebase_UID: user.uid,
                date: currentDate,
                total: totals.totalGeneral,
                paymentMethod: parseInt(paymentMethod),
                items: transformedItems
            };

            console.log('Paso 1: Creando venta con abono...', salePayload);
            
            const saleResponse = await dotoniwoofApi.post("/sale.php/installment", salePayload);
            
            if (saleResponse.data.code !== 201 && saleResponse.data.code !== 200) {
                throw new Error('Error al crear la venta');
            }

            const idSaleShop = saleResponse.data.idSaleShop;
            console.log('Venta creada con ID:', idSaleShop);
            
            // PASO 2: Crear el payment plan
            const paymentPlanPayload = {
                firebase_UID: user.uid,
                name: installmentData.name,
                number_of_installments: 1,
                create_date: currentDate,
                phone: installmentData.phone,
                amount: totals.totalGeneral
            };

            console.log('Paso 2: Creando plan de pago...', paymentPlanPayload);
            
            const paymentPlanResponse = await dotoniwoofApi.post("/paymentPlan.php", paymentPlanPayload);
            
            if (paymentPlanResponse.data.code !== 201 && paymentPlanResponse.data.code !== 200) {
                throw new Error('Error al crear el plan de pago');
            }

            const idPaymentPlan = paymentPlanResponse.data.id_payment_plan;
            console.log('Plan de pago creado con ID:', idPaymentPlan);
            
            // PASO 3: Crear el installment
            const installmentPayload = {
                firebase_UID: user.uid,
                amount: parseFloat(installmentData.amount),
                id_sale: idSaleShop,
                id_payment_plan: idPaymentPlan,
                create_date: currentDate,
                installment_number: 1,
                total_sale: totals.totalGeneral
            };

            console.log('Paso 3: Creando cuota de abono...', installmentPayload);
            
            const installmentResponse = await dotoniwoofApi.post("/installment.php", installmentPayload);
            
            if (installmentResponse.data.code !== 201 && installmentResponse.data.code !== 200) {
                throw new Error('Error al crear la cuota de abono');
            }

            console.log('Abono creado exitosamente');
            
            // Todo exitoso - mostrar alerta y limpiar
            showAlert('Abono registrado exitosamente', 'success');
            generatePDF();
            clearCart();
            clearSaleDraft(user?.uid);
            setInstallmentData({ phone: '', name: '', amount: '' });
            getItems(); // Recargar items para actualizar stock
            
        } catch (error) {
            console.error('Error al procesar el abono:', error);
            showAlert('Error al procesar el abono. Por favor, intente nuevamente.', 'error');
        } finally {
            setLoading(false);
        }
    };


    const generatePDF = () => {
        const doc = new jsPDF({ unit: 'mm', format: [80, 200] }); // Formato ticket POS estándar
        doc.setFont('helvetica');
        
        // Configuración para ticket POS
        const pageWidth = 80;
        const margin = 5;
        const contentWidth = pageWidth - (margin * 2);
        
        // Header del negocio
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('DOTONIWOOF', pageWidth / 2, 10, { align: 'center' });
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text('Nit: 901.981.483-0', pageWidth / 2, 15, { align: 'center' });
        doc.text('Dirección: Cra 22 g # 59 a - 31', pageWidth / 2, 18, { align: 'center' });
        doc.text('Tel: (601) 234-5678', pageWidth / 2, 21, { align: 'center' });
        
        // Línea separadora
        doc.setLineWidth(0.5);
        doc.line(margin, 24, pageWidth - margin, 24);
        
        // Información de la venta
        doc.setFontSize(8);
        doc.text(`Fecha: ${new Date().toLocaleDateString('es-CO')}`, margin, 28);
        doc.text(`Hora: ${new Date().toLocaleTimeString('es-CO')}`, margin, 31);
        doc.text(`Vendedor: ${user.email}`, margin, 34);
        
        // Línea separadora
        doc.line(margin, 36, pageWidth - margin, 36);
        
        // Items de la venta
        doc.setFontSize(7);
        doc.text('DESCRIPCIÓN', margin, 40);
        doc.text('CANT', margin + 35, 40);
        doc.text('PRECIO', margin + 45, 40);
        doc.text('TOTAL', margin + 65, 40);
        
        // Línea separadora
        doc.line(margin, 41, pageWidth - margin, 41);
        
        let yPosition = 45;
        
        selectedItems.forEach((item) => {
            const itemSalePrice = parseFloat(item.salePrice || item.price);
            const itemQuantity = parseInt(item.quantity || 1);
            const itemTotal = itemSalePrice * itemQuantity;
            
            // Nombre del producto con atributos (máximo 2 líneas)
            const productName = getItemDisplayName(item);
            const maxCharsPerLine = 20;
            
            if (productName.length > maxCharsPerLine) {
                const line1 = productName.substring(0, maxCharsPerLine);
                const line2 = productName.substring(maxCharsPerLine, maxCharsPerLine * 2);
                doc.text(line1, margin, yPosition);
                if (line2) {
                    doc.text(line2, margin, yPosition + 3);
                    yPosition += 3;
                }
            } else {
                doc.text(productName, margin, yPosition);
            }
            
            // Cantidad
            doc.text(itemQuantity.toString(), margin + 35, yPosition);
            
            // Precio unitario
            doc.text(formatter.format(itemSalePrice), margin + 45, yPosition);
            
            // Total
            doc.text(formatter.format(itemTotal), margin + 65, yPosition);
            
            yPosition += 6;
        });
        
        // Línea separadora
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 3;
        
        // Totales
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        
        // Subtotal
        doc.text('SUBTOTAL:', margin, yPosition);
        doc.text(formatter.format(totals.subtotal), margin + 65, yPosition, { align: 'right' });
        yPosition += 4;
        
        // IVA
        if (totals.totalIVA > 0) {
            doc.text('IVA:', margin, yPosition);
            doc.text(formatter.format(totals.totalIVA), margin + 65, yPosition, { align: 'right' });
            yPosition += 4;
        }
        
        // Total
        doc.setFontSize(10);
        doc.text('TOTAL:', margin, yPosition);
        doc.text(formatter.format(totals.totalGeneral), margin + 65, yPosition, { align: 'right' });
        yPosition += 6;
        
        // Línea separadora
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 4;
        
        // Método de pago
        const paymentMethodName = payments.find(p => p.id_payment_method == paymentMethod)?.method_name || 'No especificado';
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`Método de pago: ${paymentMethodName}`, margin, yPosition);
        yPosition += 4;
        
        // Cliente si existe
        if (selectedCustomer) {
            doc.text(`Cliente: ${selectedCustomer.name || selectedCustomer.customer_name}`, margin, yPosition);
            yPosition += 4;
        }
        
        // Línea separadora
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 4;
        
        // Footer
        doc.setFontSize(6);
        doc.text('¡Gracias por su compra!', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 3;
        doc.text('Conserve este comprobante', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 3;
        doc.text('www.dotoniwoof.com', pageWidth / 2, yPosition, { align: 'center' });
        
        // Generar PDF
        const pdfUrl = doc.output('bloburl');
        window.open(pdfUrl, '_blank');
    };

    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <>
            <Helmet>
                <title>Registro de Ventas - DotoniWoof</title>
            </Helmet>
            <NavContainer />
            <OffCanvasNav />
            <LoadingOverlay show={loading} />
            
            <Container fluid className="modern-register-sale-container">
                <Row className="h-100">
                    {/* Panel de Productos */}
                    <Col lg={8} className="products-panel">
                        <Card className="h-100 modern-card">
                            <Card.Header className="modern-card-header">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <h4 className="mb-0">
                                        <i className="fas fa-store me-2"></i>
                                        Productos Disponibles
                                    </h4>
                                    <div className="d-flex align-items-center gap-2">
                                        <Button
                                            variant="outline-light"
                                            size="sm"
                                            onClick={handleGoToCreateProduct}
                                            title="¿No encuentras un producto? Créalo aquí"
                                        >
                                            <i className="fas fa-plus me-1"></i>
                                            Crear producto
                                        </Button>
                                        <Badge bg="primary" className="fs-6">
                                            {filteredItems.length} productos
                                        </Badge>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {/* Barra de Búsqueda */}
                                <div className="search-section p-3 border-bottom">
                                    <Row className="g-2">
                                        <Col md={8}>
                                            <InputGroup size="lg">
                                                <InputGroup.Text>
                                                    <i className="fas fa-search"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Buscar por nombre o SKU..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    className="modern-search-input"
                                                />
                                                <Button 
                                                    variant="outline-secondary" 
                                                    onClick={() => handleInputFocus('searchTerm', 'text')}
                                                    title="Abrir teclado virtual"
                                                >
                                                    <i className="fas fa-keyboard"></i>
                                                </Button>
                                            </InputGroup>
                                        </Col>
                                        <Col md={4}>
                                            <InputGroup size="lg">
                                                <InputGroup.Text>
                                                    <i className="fas fa-barcode"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Código de barras"
                                                    value={searchSKU}
                                                    onChange={(e) => setSKU(e.target.value)}
                                                    className="modern-search-input"
                                                />
                                                <Button 
                                                    variant="outline-secondary" 
                                                    onClick={() => handleInputFocus('searchSKU', 'text')}
                                                    title="Abrir teclado virtual"
                                                >
                                                    <i className="fas fa-keyboard"></i>
                                                </Button>
                                            </InputGroup>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Tabla de Productos */}
                                <div className="products-table">
                                    {loading ? (
                                        <div className="loading-container text-center p-4">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-2">Cargando productos...</p>
                                        </div>
                                    ) : (
                                        <Table responsive hover className="mb-0">
                                            <thead className="table-dark">
                                                <tr>
                                                    <th style={{ width: '64px' }}>Imagen</th>
                                                    <th>Nombre</th>
                                                    <th style={{ width: '80px' }}>Peso</th>
                                                    <th style={{ width: '100px' }}>Valor Sugerido</th>
                                                    <th style={{ width: '100px' }}>IVA</th>
                                                    <th style={{ width: '80px' }}>Acción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentItems.map((item) => (
                                                    <tr key={item.id_item} className="product-row">
                                                        <td className="text-center">
                                                            <ProductImageThumb url={item.url} alt={getItemDisplayName(item)} />
                                                        </td>
                                                        <td>
                                                            <div className="product-info">
                                                                <h6 className="mb-1 product-name">{getItemDisplayName(item)}</h6>
                                                                {item.product.sku && (
                                                                    <small className="text-muted">
                                                                        SKU: {item.product.sku}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="product-weight">
                                                                {item.weight.weight_name || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className="product-price fw-bold text-success">
                                                                {formatter.format(item.price)}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge 
                                                                bg={item.rate > 0 ? "warning" : "secondary"} 
                                                                className="fs-6"
                                                            >
                                                                {(item.rate * 100).toFixed(1)}%
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={() => handleAddToCart(item)}
                                                                className="add-button"
                                                                title="Agregar al carrito"
                                                            >
                                                                <span className="add-icon me-1">+</span>
                                                                Agregar
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </div>

                                {/* Paginación */}
                                {totalPages > 1 && (
                                    <div className="pagination-section p-3 border-top">
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={paginate}
                                        />
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Panel del Carrito */}
                    <Col lg={4} className="cart-panel">
                        <Card className="h-100 modern-card">
                            <Card.Header className="modern-card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">
                                        <i className="fas fa-shopping-cart me-2"></i>
                                        Carrito de Ventas
                                    </h4>
                                    <Badge bg="success" className="fs-6">
                                        {selectedItems.length} items
                                    </Badge>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0 cart-content">
                                {/* Lista del Carrito */}
                                <div className="cart-scrollable">
                                    <AnimatePresence>
                                        {selectedItems.length === 0 ? (
                                            <div className="empty-cart text-center p-4">
                                                <i className="fas fa-shopping-cart fa-3x text-muted mb-3"></i>
                                                <p className="text-muted">El carrito está vacío</p>
                                                <small className="text-muted">
                                                    Selecciona productos para comenzar
                                                </small>
                                            </div>
                                        ) : (
                                            <ListGroup variant="flush">
                                                {selectedItems.map((item) => {
                                                    const itemSalePrice = parseFloat(item.salePrice || item.price);
                                                    const itemQuantity = parseInt(item.quantity || 1);
                                                    const itemRate = parseFloat(item.rate) || 0;
                                                    const itemSubtotalPerUnit = itemSalePrice / (1 + itemRate);
                                                    const itemIVAPerUnit = itemSubtotalPerUnit * itemRate;
                                                    const itemSubtotal = itemSubtotalPerUnit * itemQuantity;
                                                    const itemIVA = itemIVAPerUnit * itemQuantity;
                                                    const itemTotal = itemSalePrice * itemQuantity;
                                                    
                                                    return (
                                                        <motion.div
                                                            key={item.key}
                                                            initial={{ opacity: 0, x: 50 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            exit={{ opacity: 0, x: -50 }}
                                                            transition={{ duration: 0.3 }}
                                                        >
                                                            <ListGroup.Item className="cart-item">
                                                                <div className="d-flex justify-content-between align-items-start gap-2">
                                                                    <div className="cart-item-thumb">
                                                                        <ProductImageThumb url={item.url} alt={getItemDisplayName(item)} />
                                                                    </div>
                                                                    <div className="item-info flex-grow-1">
                                                                        <h6 className="mb-1">{getItemDisplayName(item)}</h6>
                                                                        <small className="text-muted">
                                                                            {formatter.format(itemSalePrice)} c/u (IVA incluido) × {itemQuantity}
                                                                        </small>
                                                        <Badge 
                                                            bg={itemRate > 0 ? "warning" : "secondary"} 
                                                            className="ms-2"
                                                            style={{ fontSize: '0.7rem' }}
                                                        >
                                                            IVA: {(itemRate * 100).toFixed(1)}%
                                                        </Badge>
                                                                    </div>
                                                                    <div className="item-controls">
                                                                        <div className="quantity-controls mb-2">
                                                                            <Button
                                                                                variant="outline-secondary"
                                                                                size="sm"
                                                                                onClick={() => updateQuantity(item.key, (item.quantity || 1) - 1)}
                                                                                className="quantity-btn"
                                                                            >
                                                                                <span className="quantity-icon">−</span>
                                                                            </Button>
                                                                            <span className="mx-2 fw-bold">{item.quantity || 1}</span>
                                                                            <Button
                                                                                variant="outline-secondary"
                                                                                size="sm"
                                                                                onClick={() => updateQuantity(item.key, (item.quantity || 1) + 1)}
                                                                                className="quantity-btn"
                                                                            >
                                                                                <span className="quantity-icon">+</span>
                                                                            </Button>
                                                                        </div>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => removeFromCart(item.key)}
                                                                            className="remove-btn"
                                                                        >
                                                                            <span className="remove-icon">×</span>
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                                <div className="item-totals mt-2">
                                                                    <div className="d-flex justify-content-between">
                                                                        <small>Subtotal:</small>
                                                                        <small>{formatter.format(itemSubtotal)}</small>
                                                                    </div>
                                                                    {itemRate > 0 && (
                                                                        <div className="d-flex justify-content-between">
                                                                            <small>IVA ({(itemRate * 100).toFixed(1)}%):</small>
                                                                            <small>{formatter.format(itemIVA)}</small>
                                                                        </div>
                                                                    )}
                                                                    <div className="d-flex justify-content-between fw-bold">
                                                                        <span>Total:</span>
                                                                        <span className="text-success">{formatter.format(itemTotal)}</span>
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                        </motion.div>
                                                    );
                                                })}
                                            </ListGroup>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Totales Generales */}
                                {selectedItems.length > 0 && (
                                    <div className="totals-section totals-compact">
                                        <div className="total-row d-flex justify-content-between">
                                            <span>Subtotal:</span>
                                            <span>{formatter.format(totals.subtotal)}</span>
                                        </div>
                                        <div className="total-row d-flex justify-content-between">
                                            <span>IVA:</span>
                                            <span>{formatter.format(totals.totalIVA)}</span>
                                        </div>
                                        <div className="total-row total-final d-flex justify-content-between fw-bold">
                                            <span>TOTAL:</span>
                                            <span className="text-success">{formatter.format(totals.totalGeneral)}</span>
                                        </div>
                                    </div>
                                )}

        

                                {/* Método de Pago */}
                                {selectedItems.length > 0 && (
                                    <div className="payment-section payment-compact">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="payment-label">
                                                <i className="fas fa-credit-card me-1"></i>Pago
                                            </Form.Label>
                                            <Form.Select
                                                value={paymentMethod}
                                                onChange={(e) => setPaymentMethod(e.target.value)}
                                                size="sm"
                                                disabled={paymentsLoading}
                                            >
                                                <option value="">
                                                    {paymentsLoading ? 'Cargando métodos...' : 'Seleccionar método'}
                                                </option>
                                                {payments.map((payment) => (
                                                    <option key={payment.id_payment_method} value={payment.id_payment_method}>
                                                        {payment.method_name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {paymentsLoading && (
                                                <div className="text-center mt-2">
                                                    <Spinner size="sm" />
                                                    <small className="text-muted ms-2">Cargando métodos de pago...</small>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </div>
                                )}

                                {/* Botones de Acción */}
                                {selectedItems.length > 0 && (
                                    <div className="action-buttons cart-footer">
                                        <div className="action-buttons-grid">
                                            <Button
                                                variant="success"
                                                size="sm"
                                                className="action-btn-main"
                                                onClick={handleSubmitPurchase}
                                                disabled={selectedItems.length === 0 || !paymentMethod || loading}
                                            >
                                                {loading ? (
                                                    <Spinner size="sm" className="me-1" />
                                                ) : (
                                                    <i className="fas fa-check-circle me-1"></i>
                                                )}
                                                {loading ? 'Procesando...' : 'Finalizar'}
                                            </Button>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="action-btn-compact"
                                                onClick={handleSaveDraft}
                                                title="Guarda la venta para continuar después"
                                            >
                                                <i className="fas fa-save"></i>
                                            </Button>
                                            <Button
                                                variant="outline-info"
                                                size="sm"
                                                className="action-btn-compact"
                                                onClick={handleGoToCreateProduct}
                                                title="Ir a crear producto. La venta se guardará como borrador."
                                            >
                                                <i className="fas fa-plus-circle"></i>
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="action-btn-compact"
                                                onClick={clearCart}
                                                title="Limpiar carrito"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Modal de Precio de Venta */}
                <Modal show={showPriceModal} onHide={cancelAddToCart} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="fas fa-dollar-sign me-2"></i>
                            Precio de Venta
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedItemForPrice && (
                            <div className="mb-3 d-flex gap-3 align-items-start">
                                <div className="price-modal-thumb">
                                    <ProductImageThumb url={selectedItemForPrice.url} alt={getItemDisplayName(selectedItemForPrice)} />
                                </div>
                                <div className="flex-grow-1">
                                <h6 className="text-primary">{getItemDisplayName(selectedItemForPrice)}</h6>
                                <div className="row text-muted small">
                                    <div className="col-6">
                                        <strong>Precio base:</strong> {formatter.format(selectedItemForPrice.price)}
                                    </div>
                                    <div className="col-6">
                                        <strong>IVA:</strong> {(parseFloat(selectedItemForPrice.rate || 0) * 100).toFixed(1)}%
                                    </div>
                                </div>
                                <div className="mt-2 p-2 bg-light rounded">
                                    <small className="text-success">
                                        <i className="fas fa-info-circle me-1"></i>
                                        El precio de venta debe incluir el IVA
                                    </small>
                                </div>
                                </div>
                            </div>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Precio de Venta (IVA incluido)</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    value={salePrice}
                                    onChange={(e) => setSalePrice(e.target.value)}
                                    placeholder="Ingrese el precio de venta"
                                    min="0"
                                    step="0.01"
                                    autoFocus
                                />
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => handleInputFocus('salePrice', 'number')}
                                    title="Abrir teclado virtual"
                                >
                                    <i className="fas fa-keyboard"></i>
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Precio sugerido: {formatter.format(parseFloat(selectedItemForPrice?.price || 0) * (1 + parseFloat(selectedItemForPrice?.rate || 0)))}
                            </Form.Text>
                        </Form.Group>
                        
                        <Form.Group>
                            <Form.Label>Cantidad</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>
                                    <i className="fas fa-hashtag"></i>
                                </InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    placeholder="Cantidad"
                                    min="1"
                                    step="1"
                                />
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => handleInputFocus('quantity', 'number')}
                                    title="Abrir teclado virtual"
                                >
                                    <i className="fas fa-keyboard"></i>
                                </Button>
                            </InputGroup>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={cancelAddToCart}>
                            <i className="fas fa-times me-2"></i>
                            Cancelar
                        </Button>
                        <Button variant="success" onClick={confirmAddToCart} disabled={!salePrice || parseFloat(salePrice) <= 0 || !quantity || parseInt(quantity) <= 0}>
                            <i className="fas fa-check me-2"></i>
                            Agregar al Carrito
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal de Abonos */}
                <Modal show={showInstallmentModal} onHide={() => setShowInstallmentModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="fas fa-money-bill-wave me-2"></i>
                            Registrar Abono
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-3">
                            <Alert variant="info">
                                <strong>Total de la venta: {formatter.format(totals.totalGeneral)}</strong>
                            </Alert>
                        </div>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <i className="fas fa-phone me-2"></i>
                                Número de Teléfono del Cliente
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={installmentData.phone}
                                    onChange={(e) => setInstallmentData({...installmentData, phone: e.target.value})}
                                    placeholder="Ingrese el número de teléfono"
                                    required
                                />
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => handleInputFocus('installmentPhone', 'tel')}
                                    title="Abrir teclado numérico"
                                >
                                    <i className="fas fa-keyboard"></i>
                                </Button>
                            </InputGroup>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <i className="fas fa-user me-2"></i>
                                Nombre del Cliente
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type="text"
                                    value={installmentData.name}
                                    onChange={(e) => setInstallmentData({...installmentData, name: e.target.value})}
                                    placeholder="Ingrese el nombre completo"
                                    required
                                />
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => handleInputFocus('installmentName', 'text')}
                                    title="Abrir teclado virtual"
                                >
                                    <i className="fas fa-keyboard"></i>
                                </Button>
                            </InputGroup>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <i className="fas fa-dollar-sign me-2"></i>
                                Valor del Abono
                            </Form.Label>
                            <InputGroup>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    inputMode="decimal"
                                    value={installmentData.amount}
                                    onChange={(e) => setInstallmentData({...installmentData, amount: e.target.value})}
                                    placeholder="Ingrese el valor del abono"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => handleInputFocus('installmentAmount', 'number')}
                                    title="Abrir teclado numérico"
                                >
                                    <i className="fas fa-keyboard"></i>
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                El abono debe ser menor o igual al total de la venta
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowInstallmentModal(false)}>
                            <i className="fas fa-times me-2"></i>
                            Cancelar
                        </Button>
                        <Button 
                            variant="success" 
                            onClick={handleCreateInstallment}
                            disabled={
                                !installmentData.phone || 
                                !installmentData.name || 
                                !installmentData.amount ||
                                parseFloat(installmentData.amount) <= 0 ||
                                parseFloat(installmentData.amount) > totals.totalGeneral
                            }
                        >
                            <i className="fas fa-check me-2"></i>
                            Crear Abono
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal: Recuperar venta guardada */}
                <Modal show={showDraftModal} onHide={handleDiscardDraft} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="fas fa-file-alt me-2 text-primary"></i>
                            Venta guardada
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="mb-0">
                            Tienes una venta guardada como borrador. ¿Deseas continuar donde la dejaste?
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={handleDiscardDraft}>
                            <i className="fas fa-times me-2"></i>
                            Descartar y empezar nueva
                        </Button>
                        <Button variant="primary" onClick={handleRestoreDraft}>
                            <i className="fas fa-undo me-2"></i>
                            Continuar venta guardada
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Alertas */}
                {/* Modal de Alerta Temporal */}
                <AlertModal
                    show={showAlertModal}
                    onHide={hideAlert}
                    message={alertMessage}
                    type={alertType}
                />

                {/* Modal de Teclado Virtual */}
                <Modal 
                    show={showKeyboard} 
                    onHide={handleCloseKeyboard}
                    centered
                    size={keyboardType === 'numeric' ? 'sm' : 'lg'}
                    className="keyboard-modal"
                    backdrop={true}
                    keyboard={true}
                >
                    <Modal.Header closeButton className="keyboard-modal-header">
                        <Modal.Title>
                            <i className="fas fa-keyboard me-2"></i>
                            {keyboardType === 'numeric' ? 'Teclado Numérico' : 'Teclado Alfanumérico'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="keyboard-modal-body">
                        <div className="keyboard-input-preview">
                            <span className="keyboard-input-preview-label">Texto:</span>
                            <div className="keyboard-input-preview-value">
                                {getCurrentInputValue() || <span className="keyboard-input-preview-placeholder">Escriba aquí...</span>}
                            </div>
                        </div>
                        <Keyboard
                            keyboardRef={r => (keyboardRef.current = r)}
                            layoutName="default"
                            onChange={handleKeyboardChange}
                            input={getCurrentInputValue()}
                            theme="hg-theme-default hg-layout-numeric"
                            layout={keyboardType === 'numeric' ? {
                                default: [
                                    "7 8 9",
                                    "4 5 6",
                                    "1 2 3",
                                    "0 . {bksp}"
                                ]
                            } : {
                                default: [
                                    "1 2 3 4 5 6 7 8 9 0 {bksp}",
                                    "q w e r t y u i o p",
                                    "a s d f g h j k l ñ",
                                    "z x c v b n m , . -",
                                    "{space}"
                                ]
                            }}
                            display={{
                                "{bksp}": "⌫",
                                "{space}": "Espacio"
                            }}
                            buttonTheme={[
                                {
                                    class: "hg-button-bksp",
                                    buttons: "{bksp}"
                                }
                            ]}
                        />
                    </Modal.Body>
                    <Modal.Footer className="keyboard-modal-footer">
                        <Button 
                            variant="success" 
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCloseKeyboard();
                            }}
                            className="w-100"
                            type="button"
                        >
                            <i className="fas fa-check me-2"></i>
                            Aceptar
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </>
    );
};

export default ModernRegisterSale;
